import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, X } from 'lucide-react';

const FullScreenMenu = ({ isOpen, onClose }) => {
  const [hoveredItem, setHoveredItem] = useState(null);

  const topMenuItem = {
    name: 'Living with Artourius',
    path: '/ideas-impressions'
  };

  const pairedMenuItems = [
    {
      left: { name: 'Lightings', subtitle: 'Illuminated elegance by our Chicken Cage designs', path: '/bird-cage-lightings' },
      right: { name: 'Hand Crafted Sculptures', subtitle: 'Sculptural masterpieces', path: '/sculpture' }
    },
    {
      left: { name: 'Coffee Tables', subtitle: 'Functional luxury centerpieces', path: '#', comingSoon: true },
      right: { name: 'Mixed Art Pieces', subtitle: 'Eclectic creative expressions', path: '#', comingSoon: true }
    },
    {
      left: { name: 'Lighting Configurator', subtitle: 'Find your perfect light', path: '#', comingSoon: true },
      right: { name: 'Statue Configurator', subtitle: 'Create the Statue of your Desire', path: '#', comingSoon: true }
    }
  ];

  const companyItems = [
    { name: 'ABOUT US', path: '/about', subtitle: 'Our story & craftsmanship' },
    { name: 'CONTACT', path: '/contact', subtitle: 'Get in touch with us' }
  ];

  return (
    <>
      {/* Fullscreen Menu Overlay */}
      <div
        className={`fixed inset-0 z-50 bg-black transition-all duration-700 ${
          isOpen
            ? 'opacity-100 pointer-events-auto'
            : 'opacity-0 pointer-events-none'
        }`}
        style={{
          backdropFilter: isOpen ? 'blur(20px)' : 'none'
        }}
      >
        {/* Animated Background Gradient */}
        <div className="absolute inset-0 overflow-hidden">
          <div
            className={`absolute top-1/4 left-1/4 w-96 h-96 bg-[hsl(45,100%,70%)] rounded-full blur-3xl transition-all duration-1000 ${
              isOpen ? 'opacity-10 scale-100' : 'opacity-0 scale-50'
            }`}
          />
          <div
            className={`absolute bottom-1/4 right-1/4 w-96 h-96 bg-[hsl(45,100%,70%)] rounded-full blur-3xl transition-all duration-1000 delay-200 ${
              isOpen ? 'opacity-10 scale-100' : 'opacity-0 scale-50'
            }`}
          />
        </div>

        {/* Close Button */}
        <button
          onClick={onClose}
          className={`absolute top-8 right-8 w-14 h-14 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center hover:bg-[hsl(45,100%,70%)] hover:rotate-90 transition-all duration-300 group z-50 ${
            isOpen ? 'opacity-100 scale-100' : 'opacity-0 scale-50'
          }`}
          aria-label="Close menu"
        >
          <X className="w-7 h-7 text-white group-hover:text-black transition-colors" />
        </button>

        {/* Menu Content */}
        <div className="relative h-full flex items-center justify-center px-8 md:px-16 lg:px-24">
          <div className="w-full max-w-7xl">
            
            {/* Top Centered Item - Living with Artourius */}
            <div className="text-center mb-16">
              <div
                className={`transition-all duration-700 ${
                  isOpen ? 'opacity-100' : 'opacity-0'
                }`}
              >
                <Link
                  to={topMenuItem.path}
                  onClick={onClose}
                  onMouseEnter={() => setHoveredItem('top')}
                  onMouseLeave={() => setHoveredItem(null)}
                  className="group inline-block"
                >
                  <div
                    className={`transform transition-all duration-700 ${
                      isOpen ? 'translate-y-0 opacity-100' : '-translate-y-10 opacity-0'
                    }`}
                  >
                    <h1
                      className={`text-5xl md:text-6xl lg:text-7xl font-bold text-white transition-all duration-500 pb-2 ${
                        hoveredItem === 'top' ? 'text-[hsl(45,100%,70%)] scale-105' : ''
                      }`}
                      style={{
                        fontWeight: '700',
                        letterSpacing: '-0.02em'
                      }}
                    >
                      {topMenuItem.name}
                    </h1>
                    <p
                      className={`text-gray-400 text-lg md:text-xl mt-3 transition-all duration-500 ${
                        hoveredItem === 'top' ? 'text-[hsl(45,100%,70%)]' : ''
                      }`}
                    >
                      Interior Inspirations - Lifestyle Gallery
                    </p>
                  </div>
                </Link>
              </div>
            </div>

            {/* Paired Menu Items - Left/Right */}
            <div className="space-y-8 mb-16">
              {pairedMenuItems.map((pair, pairIndex) => (
                <div key={pairIndex} className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Left Item */}
                  <div className="overflow-hidden">
                    {pair.left.comingSoon ? (
                      <div
                        onMouseEnter={() => setHoveredItem(`left-${pairIndex}`)}
                        onMouseLeave={() => setHoveredItem(null)}
                        className="group block cursor-not-allowed"
                      >
                        <div
                          className={`transform transition-all duration-700 ${
                            isOpen ? 'translate-x-0 opacity-100' : '-translate-x-full opacity-0'
                          }`}
                          style={{
                            transitionDelay: isOpen ? `${(pairIndex + 1) * 100}ms` : '0ms'
                          }}
                        >
                          <div className="py-4 border-b border-gray-800 group-hover:border-[hsl(45,100%,70%)] transition-colors duration-300">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-600">
                                {pair.left.name}
                              </h3>
                              <span className="px-3 py-1 bg-[hsl(45,100%,70%)]/20 text-[hsl(45,100%,70%)] text-xs font-bold rounded-full">
                                COMING SOON
                              </span>
                            </div>
                            <p className="text-gray-500 text-sm md:text-base">
                              {pair.left.subtitle}
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : pair.left.external ? (
                      <a
                        href={pair.left.path}
                        target="_blank"
                        rel="noopener noreferrer"
                        onMouseEnter={() => setHoveredItem(`left-${pairIndex}`)}
                        onMouseLeave={() => setHoveredItem(null)}
                        className="group block"
                      >
                        <div
                          className={`transform transition-all duration-700 ${
                            isOpen ? 'translate-x-0 opacity-100' : '-translate-x-full opacity-0'
                          }`}
                          style={{
                            transitionDelay: isOpen ? `${(pairIndex + 1) * 100}ms` : '0ms'
                          }}
                        >
                          <div className="py-4 border-b border-gray-800 group-hover:border-[hsl(45,100%,70%)] transition-colors duration-300">
                            <h3
                              className={`text-2xl md:text-3xl lg:text-4xl font-bold text-white group-hover:text-[hsl(45,100%,70%)] transition-all duration-500 mb-2 ${
                                hoveredItem === `left-${pairIndex}` ? 'translate-x-4' : 'translate-x-0'
                              }`}
                            >
                              {pair.left.name}
                            </h3>
                            <p className="text-gray-400 text-sm md:text-base">
                              {pair.left.subtitle}
                            </p>
                          </div>
                        </div>
                      </a>
                    ) : (
                      <Link
                        to={pair.left.path}
                        onClick={onClose}
                        onMouseEnter={() => setHoveredItem(`left-${pairIndex}`)}
                        onMouseLeave={() => setHoveredItem(null)}
                        className="group block"
                      >
                        <div
                          className={`transform transition-all duration-700 ${
                            isOpen ? 'translate-x-0 opacity-100' : '-translate-x-full opacity-0'
                          }`}
                          style={{
                            transitionDelay: isOpen ? `${(pairIndex + 1) * 100}ms` : '0ms'
                          }}
                        >
                          <div className="py-4 border-b border-gray-800 group-hover:border-[hsl(45,100%,70%)] transition-colors duration-300">
                            <h3
                              className={`text-2xl md:text-3xl lg:text-4xl font-bold text-white group-hover:text-[hsl(45,100%,70%)] transition-all duration-500 mb-2 ${
                                hoveredItem === `left-${pairIndex}` ? 'translate-x-4' : 'translate-x-0'
                              }`}
                            >
                              {pair.left.name}
                            </h3>
                            <p className="text-gray-400 text-sm md:text-base">
                              {pair.left.subtitle}
                            </p>
                          </div>
                        </div>
                      </Link>
                    )}
                  </div>

                  {/* Right Item */}
                  <div className="overflow-hidden">
                    {!pair.right ? (
                      <div className="py-4"></div>
                    ) : pair.right.comingSoon ? (
                      <div
                        onMouseEnter={() => setHoveredItem(`right-${pairIndex}`)}
                        onMouseLeave={() => setHoveredItem(null)}
                        className="group block cursor-not-allowed"
                      >
                        <div
                          className={`transform transition-all duration-700 ${
                            isOpen ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'
                          }`}
                          style={{
                            transitionDelay: isOpen ? `${(pairIndex + 1) * 100 + 50}ms` : '0ms'
                          }}
                        >
                          <div className="py-4 border-b border-gray-800 group-hover:border-[hsl(45,100%,70%)] transition-colors duration-300">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-600">
                                {pair.right.name}
                              </h3>
                              <span className="px-3 py-1 bg-[hsl(45,100%,70%)]/20 text-[hsl(45,100%,70%)] text-xs font-bold rounded-full">
                                COMING SOON
                              </span>
                            </div>
                            <p className="text-gray-500 text-sm md:text-base">
                              {pair.right.subtitle}
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : pair.right.external ? (
                      <a
                        href={pair.right.path}
                        target="_blank"
                        rel="noopener noreferrer"
                        onMouseEnter={() => setHoveredItem(`right-${pairIndex}`)}
                        onMouseLeave={() => setHoveredItem(null)}
                        className="group block"
                      >
                        <div
                          className={`transform transition-all duration-700 ${
                            isOpen ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'
                          }`}
                          style={{
                            transitionDelay: isOpen ? `${(pairIndex + 1) * 100 + 50}ms` : '0ms'
                          }}
                        >
                          <div className="py-4 border-b border-gray-800 group-hover:border-[hsl(45,100%,70%)] transition-colors duration-300">
                            <h3
                              className={`text-2xl md:text-3xl lg:text-4xl font-bold text-white group-hover:text-[hsl(45,100%,70%)] transition-all duration-500 mb-2 ${
                                hoveredItem === `right-${pairIndex}` ? 'translate-x-4' : 'translate-x-0'
                              }`}
                            >
                              {pair.right.name}
                            </h3>
                            <p className="text-gray-400 text-sm md:text-base">
                              {pair.right.subtitle}
                            </p>
                          </div>
                        </div>
                      </a>
                    ) : (
                      <Link
                        to={pair.right.path}
                        onClick={onClose}
                        onMouseEnter={() => setHoveredItem(`right-${pairIndex}`)}
                        onMouseLeave={() => setHoveredItem(null)}
                        className="group block"
                      >
                        <div
                          className={`transform transition-all duration-700 ${
                            isOpen ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'
                          }`}
                          style={{
                            transitionDelay: isOpen ? `${(pairIndex + 1) * 100 + 50}ms` : '0ms'
                          }}
                        >
                          <div className="py-4 border-b border-gray-800 group-hover:border-[hsl(45,100%,70%)] transition-colors duration-300">
                            <h3
                              className={`text-2xl md:text-3xl lg:text-4xl font-bold text-white group-hover:text-[hsl(45,100%,70%)] transition-all duration-500 mb-2 ${
                                hoveredItem === `right-${pairIndex}` ? 'translate-x-4' : 'translate-x-0'
                              }`}
                            >
                              {pair.right.name}
                            </h3>
                            <p className="text-gray-400 text-sm md:text-base">
                              {pair.right.subtitle}
                            </p>
                          </div>
                        </div>
                      </Link>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Company Links */}
            <div
              className={`flex flex-col sm:flex-row justify-center gap-8 transition-all duration-700 ${
                isOpen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{
                transitionDelay: isOpen ? '800ms' : '0ms'
              }}
            >
              {companyItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={onClose}
                  className="text-gray-400 hover:text-[hsl(45,100%,70%)] transition-colors text-sm tracking-wider"
                >
                  {item.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FullScreenMenu;
